import type { CancelToken } from 'axios';
import { CONFIG } from '~/_shared/constants/config';
import {
  apiGet, apiPost, retryApiRequest,
} from '~/_shared/utils/api/api.helpers';
import { buildUrl } from '~/_shared/utils/url/url.helpers';
import type { AgsReportCountry } from '~/exportReport/exportReport.constants';

export type AgsReportRingDataRequest = {
  varlistId: string;
  country: AgsReportCountry;
  radiiInMiles: number[];
  lat: number;
  lng: number;
  returnType?: 'json' | 'pdf';
  mapId?: number;
};

export type AgsReportRingDataResponse = {
  latitude: number;
  longitude: number;
  areas: ReadonlyArray<ReportAreaResponse>;
  timeInSeconds: number;
  message: string;
};

export type AgsReportPolygonDataRequest = {
  varlistId: string;
  polygon: string;
  label?: string;
  returnType?: 'json' | 'pdf';
  country: AgsReportCountry;
  mapId?: number;
};

export type AgsReportPolygonResponse = {
  latitude: number;
  longitude: number;
  areas: ReadonlyArray<ReportAreaResponse>;
  timeInSeconds: number;
  message: string;
};

type ReportDataItem = {
  tag: string;
  description: string;
  value: string;
};

export type ReportAreaResponse = ReportArea | ReportAreaEmpty;

type ReportArea = {
  type: string;
  extent: number;
  label: string;
  data: ReportDataItem[];
};

type ReportAreaEmpty = {
  type: null;
  extent: number;
  label: null;
  data: null;
};

export function getAgsReportRingData(clientId: number, request: AgsReportRingDataRequest & { returnType: 'pdf' }, cancelToken?: CancelToken): Promise<Blob>;
export function getAgsReportRingData(clientId: number, request: AgsReportRingDataRequest, cancelToken?: CancelToken): Promise<{ report: AgsReportRingDataResponse }>;
export function getAgsReportRingData(clientId: number, {
  varlistId,
  country,
  lat,
  lng,
  radiiInMiles,
  returnType = 'json',
  mapId,
}: AgsReportRingDataRequest, cancelToken?: CancelToken): Promise<Blob | { report: AgsReportRingDataResponse }> {
  if (CONFIG.AGS_API_KEY) {
    return getAgsReportRingDataLocalProxy({ varlistId, country, lat, lng, radiiInMiles, returnType, mapId }, cancelToken);
  }

  return retryApiRequest(() => apiGet(`/api/clients/${clientId}/ags/export-report-ring`, {
    radii: radiiInMiles.join(','),
    varlist: varlistId,
    country,
    latitude: lat,
    longitude: lng,
    returnType,
    empty: true,
    map_id: mapId,
  }, {
    cancelToken,
    responseType: returnType === 'pdf' ? 'blob' : 'json',
  }));
}

export const getAgsPolygonReportData = (clientId: number, {
  varlistId,
  country,
  polygon,
  label,
  returnType = 'json',
  mapId,
}: AgsReportPolygonDataRequest, cancelToken?: CancelToken): Promise<{ report: AgsReportPolygonResponse }> => {
  if (CONFIG.AGS_API_KEY) {
    return getAgsPolygonReportDataLocalProxy({ varlistId, country, polygon, label, returnType, mapId }, cancelToken);
  }

  const requestUrl = `/api/clients/${clientId}/ags/export-report-polygon`;

  return retryApiRequest(() => apiPost(requestUrl, {
    pgn: polygon,
    varlist: varlistId,
    country,
    label,
    returnType,
    empty: true,
    map_id: mapId,
  }, {
    cancelToken,
  }));
};

export function getAgsReportRingDataLocalProxy(request: AgsReportRingDataRequest & { returnType: 'pdf' }, cancelToken?: CancelToken): Promise<Blob>;
export function getAgsReportRingDataLocalProxy(request: AgsReportRingDataRequest, cancelToken?: CancelToken): Promise<{ report: AgsReportRingDataResponse }>;
export function getAgsReportRingDataLocalProxy({
  varlistId,
  country,
  lat,
  lng,
  radiiInMiles,
  returnType = 'json',
}: AgsReportRingDataRequest, cancelToken?: CancelToken): Promise<Blob | { report: AgsReportRingDataResponse }> {
  return retryApiRequest(() => apiGet('/ags/ReportsAuth/ring/', {
    APIkey: CONFIG.AGS_API_KEY,
    radii: radiiInMiles.join(','),
    varlist: varlistId,
    country,
    latitude: lat,
    longitude: lng,
    returnType,
    empty: true,
  }, {
    cancelToken,
    responseType: returnType === 'pdf' ? 'blob' : 'json',
  }));
}

export const getAgsPolygonReportDataLocalProxy = ({
  varlistId,
  country,
  polygon,
  label,
  returnType = 'json',
}: AgsReportPolygonDataRequest, cancelToken?: CancelToken): Promise<{ report: AgsReportPolygonResponse }> => {
  const requestUrl = buildUrl('/ags/ReportsAuth/pgnWKTbody/', {
    APIkey: CONFIG.AGS_API_KEY,
    varlist: varlistId,
    country,
    label,
    returnType,
    empty: true,
  });

  return retryApiRequest(() => apiPost(requestUrl, polygon, {
    cancelToken,
    headers: {
      'Content-Type': 'application/json',
    },
  }));
};
